'use client';

import * as React from 'react';
import {Box, Typography} from '@purpose/ui/components';
import {ImageWithFallback} from '@purpose/ui/components/ImageWithFallback';
import Tag from '@purpose/ui/components/Tag/Tag';
import {cn} from '@purpose/ui/lib/utils';

interface LabeledDataGroupProps {
  title?: string;
  items: {
    title: string;
    value: string;
    icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  }[];
}

const LabeledDataGroup = ({title, items}: LabeledDataGroupProps) => {
  return (
    <div className="flex w-[560px] flex-col gap-3 py-4">
      {title && (
        <Typography variant="h4" className="text-black">
          {title}
        </Typography>
      )}
      {items.map(({title: itemTitle, value, icon: Icon}, index) => (
        <div key={`${index}_${itemTitle}`} className="flex justify-between">
          <Typography variant="body1" className="font-semibold text-black">
            {itemTitle}
          </Typography>
          <Typography variant="body2" className="text-primary-base w-[280px]">
            {Icon && <Icon className="mr-2 inline h-4 w-4" />}
            {value}
          </Typography>
        </div>
      ))}
    </div>
  );
};

interface SingleEntityDetailsOverviewProps {
  infoBlocks?: {
    title?: string;
    items: {
      title: string;
      value: string;
      icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    }[];
  }[];
  tagProps?: {
    title: string;
    className?: string;
  };
  imageSrc?: string;
  name: string;
  renderButton?: () => React.ReactNode;
}

const SingleEntityDetailsOverview = ({
  infoBlocks,
  imageSrc,
  name,
  tagProps,
  renderButton
}: SingleEntityDetailsOverviewProps) => {
  return (
    <div className="flex w-full flex-col items-center">
      <div
        className={cn(
          'flex w-[560px] gap-2 py-4',
          renderButton && 'justify-between'
        )}
      >
        <div className="flex gap-4">
          <ImageWithFallback
            src={imageSrc}
            height={88}
            width={88}
            iconStyles={{width: 50, height: 50}}
            style={{borderRadius: '50%'}}
          />
          <div className="flex flex-col justify-center gap-1">
            <Typography variant="subtitle1" className="text-black">
              {name}
            </Typography>
            {tagProps && (
              <div>
                <Tag
                  title={tagProps.title}
                  className={cn('bg-tag-bg text-tag-text', tagProps?.className)}
                />
              </div>
            )}
          </div>
        </div>
        {renderButton?.()}
      </div>
      {infoBlocks?.map((infoBlock, index) => (
        <div
          key={`${index}_${infoBlock.title}`}
          className="flex w-full justify-center"
          style={{
            borderBottom:
              index === infoBlocks.length - 1
                ? 'none'
                : '1px solid rgb(245, 245, 245)',
            borderTop: index === 0 ? '1px solid rgb(245, 245, 245)' : 'none'
          }}
        >
          <LabeledDataGroup {...infoBlock} />
        </div>
      ))}
    </div>
  );
};

export {SingleEntityDetailsOverview};
export type {SingleEntityDetailsOverviewProps};
